import { DataAPIService, ProfileAPIService } from './AxiosAPIService';
import {
  TripDetails,
  TripsResponse,
  TripByDaysResponse,
  TripsNumberResponse,
  TripsAverageSpeedResponse,
  TripsDurationResponse,
  AssignDriverToTripRequest,
  UnassignDriverFromTripRequest,
  ActiveTripsData,
  ActiveTripData,
  TripMergeSettings,
  PatchTripMergeRequest,
  PatchTripMergeResponse,
  CloseOngoingTripRequest,
  LongOngoingTripsResponse,
} from '../types/Trips';
import { logger } from './SharedService';

const TRIPS_ENDPOINT = '/trips';
const MERGED_TRIPS_ENDPOINT = '/merged-trips';
const TRIP_MERGE_ENDPOINT = '/trip-merge';

export const getTrips = async (fleetId: string, query: string): Promise<TripsResponse> => {
  logger.info('GET', 'REQUEST', 'getTrips', `${fleetId}/trips${query}`);
  const { data } = await DataAPIService.api
    .get<TripsResponse>(`${fleetId}/trips${query}`);
  logger.info('GET', 'RESPONSE', 'getTrips', data);
  return data;
};

export const getTripById = async (id: string, fleetId: string): Promise<TripDetails> => {
  logger.info('GET', 'REQUEST', 'getTripById', `${fleetId}/trips/${id}`);
  const { data } = await DataAPIService.api
    .get<TripDetails>(`${fleetId}/trips/${id}`);
  logger.info('GET', 'RESPONSE', 'getTripById', data);
  return data;
};

export const getMergedTripById = async (id: string, fleetId: string): Promise<TripDetails> => {
  logger.info('GET', 'REQUEST', 'getMergedTripById', `${fleetId}${MERGED_TRIPS_ENDPOINT}/${id}`);
  const { data } = await DataAPIService.api
    .get<TripDetails>(`${fleetId}${MERGED_TRIPS_ENDPOINT}/${id}`);
  logger.info('GET', 'RESPONSE', 'getMergedTripById', data);
  return data;
};

export const getActiveTripsData = async (
  fleetId: string,
  showStandaloneAndMergedTrips = false,
): Promise<ActiveTripsData> => {
  const query = `?showStandaloneAndMergedTrips=${showStandaloneAndMergedTrips}`;
  logger.info('GET', 'REQUEST', 'getActiveTripsData', `${fleetId}/trips/active-trips${query}`);
  const { data } = await DataAPIService.api
    .get<ActiveTripsData>(`${fleetId}/trips/active-trips${query}`);
  logger.info('GET', 'RESPONSE', 'getActiveTripsData', data);
  return data;
};

export const getActiveTripDataById = async (
  id: string,
  fleetId: string,
  showStandaloneAndMergedTrips = false,
): Promise<ActiveTripData> => {
  const query = `?showStandaloneAndMergedTrips=${showStandaloneAndMergedTrips}`;
  logger.info('GET', 'REQUEST', 'getActiveTripDataById', `${fleetId}/trips/active-trips/${id}${query}`);
  const { data } = await DataAPIService.api
    .get<ActiveTripData>(`${fleetId}/trips/active-trips/${id}${query}`);
  logger.info('GET', 'RESPONSE', 'getActiveTripDataById', data);
  return data;
};

export const getTripsByDays = async (fleetId: string, query = ''): Promise<TripByDaysResponse> => {
  logger.info('GET', 'REQUEST', 'getTripsByDays', `/${fleetId}/trips/aggregation/distanceInKm${query}`);
  const { data } = await DataAPIService.api.get<TripByDaysResponse>(`/${fleetId}/trips/aggregation/distanceInKm${query}`);
  logger.info('GET', 'RESPONSE', 'getTripsByDays', data);
  return data;
};

export const getTripsNumberByDriverId = async (fleetId: string, driverId: string): Promise<TripsNumberResponse> => {
  logger.info('GET', 'REQUEST', 'getTripsNumberByDriverId', `/${fleetId}/drivers/${driverId}/trips/aggregation/count`);
  const { data } = await DataAPIService.api.get<TripsNumberResponse>(`/${fleetId}/drivers/${driverId}/trips/aggregation/count`);
  logger.info('GET', 'RESPONSE', 'getTripsNumberByDriverId', data);
  return data;
};

export const getTripsNumberByVehicleId = async (fleetId: string, vehicleId: string): Promise<TripsNumberResponse> => {
  logger.info('GET', 'REQUEST', 'getTripsNumberByVehicleId', `/${fleetId}/vehicles/${vehicleId}/trips/aggregation/count`);
  const { data } = await DataAPIService.api.get<TripsNumberResponse>(`/${fleetId}/vehicles/${vehicleId}/trips/aggregation/count`);
  logger.info('GET', 'RESPONSE', 'getTripsNumberByVehicleId', data);
  return data;
};

export const getTripsAverageSpeedByDriverId = async (fleetId: string, driverId: string): Promise<TripsAverageSpeedResponse> => {
  logger.info('GET', 'REQUEST', 'getTripsAverageSpeedByDriverId', `/${fleetId}/drivers/${driverId}/trips/aggregation/averageSpeedMs`);
  const { data } = await DataAPIService.api.get<TripsAverageSpeedResponse>(`/${fleetId}/drivers/${driverId}/trips/aggregation/averageSpeedMs`);
  logger.info('GET', 'RESPONSE', 'getTripsAverageSpeedByDriverId', data);
  return data;
};

export const getTripsAverageSpeedByVehicleId = async (fleetId: string, vehicleId: string): Promise<TripsAverageSpeedResponse> => {
  logger.info('GET', 'REQUEST', 'getTripsAverageSpeedByVehicleId', `/${fleetId}/vehicles/${vehicleId}/trips/aggregation/averageSpeedMs`);
  const { data } = await DataAPIService.api.get<TripsAverageSpeedResponse>(`/${fleetId}/vehicles/${vehicleId}/trips/aggregation/averageSpeedMs`);
  logger.info('GET', 'RESPONSE', 'getTripsAverageSpeedByVehicleId', data);
  return data;
};

export const getTripsDurationByDriverId = async (fleetId: string, driverId: string): Promise<TripsDurationResponse> => {
  logger.info('GET', 'REQUEST', 'getTripsDurationByDriverId', `/${fleetId}/drivers/${driverId}/trips/aggregation/durationInMs`);
  const { data } = await DataAPIService.api.get<TripsDurationResponse>(`/${fleetId}/drivers/${driverId}/trips/aggregation/durationInMs`);
  logger.info('GET', 'RESPONSE', 'getTripsDurationByDriverId', data);
  return data;
};

export const getTripsDurationByVehicleId = async (fleetId: string, vehicleId: string): Promise<TripsDurationResponse> => {
  logger.info('GET', 'REQUEST', 'getTripsDurationByVehicleId', `/${fleetId}/vehicles/${vehicleId}/trips/aggregation/durationInMs`);
  const { data } = await DataAPIService.api.get<TripsDurationResponse>(`/${fleetId}/vehicles/${vehicleId}/trips/aggregation/durationInMs`);
  logger.info('GET', 'RESPONSE', 'getTripsDurationByVehicleId', data);
  return data;
};

export const assignDriverToTrip = async (payload: AssignDriverToTripRequest): Promise<TripDetails> => {
  const { fleetId, driverId, tripId } = payload;
  logger.info('POST', 'REQUEST', 'assignDriverToTrip', `/${fleetId}/trips/${tripId}/assign`, driverId);
  const { data } = await DataAPIService.api
    .post<TripDetails>(`/${fleetId}/trips/${tripId}/assign`, {
    driverId,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'assignDriverToTrip', data);
  return data;
};

export const unsssignDriverFromTrip = async (payload: UnassignDriverFromTripRequest): Promise<TripDetails> => {
  const { fleetId, tripId } = payload;
  logger.info('POST', 'REQUEST', 'unassignDriverToTrip', `/${fleetId}/trips/${tripId}/unassign`);
  const { data } = await DataAPIService.api
    .post<TripDetails>(`/${fleetId}/trips/${tripId}/unassign`)
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'unassignDriverToTrip', data);
  return data;
};

export const getLongOngoingTrips = async (fleetId: string): Promise<LongOngoingTripsResponse> => {
  logger.info('GET', 'REQUEST', 'getLongOngoingTrips', `/${fleetId}/trips/long-ongoing`);
  const { data } = await DataAPIService.api
    .get<LongOngoingTripsResponse>(`/${fleetId}/trips/long-ongoing`);
  logger.info('GET', 'RESPONSE', 'getLongOngoingTrips', data);
  return data;
};

export const closeOngoingTrip = async (payload: CloseOngoingTripRequest): Promise<TripDetails> => {
  const { fleetId, tripId, endAt } = payload;
  logger.info('POST', 'REQUEST', 'closeOngoingTrip', `/${fleetId}/trips/${tripId}/close`, { endAt });
  const { data } = await DataAPIService.api
    .post<TripDetails>(`/${fleetId}/trips/${tripId}/close`, { endAt })
    .catch((e) => Promise.reject(e.response?.data || e));
  logger.info('POST', 'RESPONSE', 'closeOngoingTrip', data);
  return data;
};

export const getTripMerge = async (fleetId: string): Promise<TripMergeSettings> => {
  logger.info('GET', 'REQUEST', 'getTripMerge', `${fleetId}${TRIP_MERGE_ENDPOINT}`);
  const { data } = await DataAPIService.api
    .get<TripMergeSettings>(`${fleetId}${TRIP_MERGE_ENDPOINT}`);
  logger.info('GET', 'RESPONSE', 'getTripMerge', data);
  return data;
};

export const patchTripMergeOnProfile = async (
  payload: PatchTripMergeRequest,
): Promise<PatchTripMergeResponse> => {
  const { fleetId, clientTripMergeEnabled } = payload;
  logger.info('PATCH', 'REQUEST', 'patchTripMergeOnProfile', `fleets/${fleetId}${TRIP_MERGE_ENDPOINT}`, { clientTripMergeEnabled });
  const { data } = await ProfileAPIService.api
    .patch<PatchTripMergeResponse>(`fleets/${fleetId}${TRIP_MERGE_ENDPOINT}`, {
    clientTripMergeEnabled,
  })
    .catch((e) => Promise.reject(e.response?.data || e));
  logger.info('PATCH', 'RESPONSE', 'patchTripMergeOnProfile', data);
  return data;
};

export const patchTripMergeOnData = async (
  payload: PatchTripMergeRequest,
): Promise<PatchTripMergeResponse> => {
  const { fleetId, clientTripMergeEnabled } = payload;
  logger.info('PATCH', 'REQUEST', 'patchTripMergeOnData', `${fleetId}${TRIP_MERGE_ENDPOINT}`, { clientTripMergeEnabled });
  const { data } = await DataAPIService.api
    .patch<PatchTripMergeResponse>(`${fleetId}${TRIP_MERGE_ENDPOINT}`, {
    clientTripMergeEnabled,
  })
    .catch((e) => Promise.reject(e.response?.data || e));
  logger.info('PATCH', 'RESPONSE', 'patchTripMergeOnData', data);
  return data;
};

export const patchTripMerge = async (payload: PatchTripMergeRequest): Promise<PatchTripMergeResponse> => {
  const [, dataResult] = await Promise.all([
    patchTripMergeOnProfile(payload),
    patchTripMergeOnData(payload),
  ]);
  return dataResult;
};
